/**
 * What failed on a service failure (cms.reschedule.servicefailuretype).
 *
 * Fixed list mirrored from the backend's SERVICE_FAILURE_TYPES in
 * app/schemas/scheduling.py — keep the two in sync (the backend 422s anything
 * else). Only "Delivery Failure" counts against On-Time Delivery on the
 * Dispatcher Metrics page; the other types still count as Service Failures.
 * Blank = untyped, which never hits OTD — so a controllable reason must carry a
 * type before the failure shows up in the metric.
 */
export const SERVICE_FAILURE_TYPES = [
  'Prepull Failure',
  'Delivery Failure',
  'Empty Return Failure',
  'Cutoff Failure',
  'SOP Failure',
] as const;

export type ServiceFailureType = (typeof SERVICE_FAILURE_TYPES)[number];

/**
 * What each type means — shown under the option in every type dropdown and in
 * the help tooltip beside the field label. Written to answer "which one do I
 * pick?" at the moment of coding.
 */
export const SERVICE_FAILURE_TYPE_DESCRIPTIONS: Record<ServiceFailureType, string> = {
  'Prepull Failure': 'The prepull failed.',
  'Delivery Failure':
    'The delivery failed: missed, rescheduled, or late for appt. The only type that counts against OTD.',
  'Empty Return Failure': 'The empty was not returned in time.',
  'Cutoff Failure': 'The cutoff was missed.',
  'SOP Failure': 'Customer requirement was not followed, e.g. wrong paperwork, incorrect check-in.',
};

export const SERVICE_FAILURE_TYPE_OPTIONS: { value: string; label: string; description: string }[] =
  SERVICE_FAILURE_TYPES.map((t) => ({ value: t, label: t, description: SERVICE_FAILURE_TYPE_DESCRIPTIONS[t] }));

/**
 * Filter options for reporting: the five types plus the Untyped bucket, which
 * is a real thing to filter on (controllable failures nobody has typed yet) even
 * though it is never a thing to SET. Kept out of the editor dropdowns above.
 */
export const SERVICE_FAILURE_TYPE_FILTER_OPTIONS: { value: string; label: string }[] = [
  ...SERVICE_FAILURE_TYPES.map((t) => ({ value: t, label: t })),
  { value: 'Untyped', label: 'Untyped' },
];

/** Controllable reasons carry the 'C:' prefix (cms.reschedulereason). */
export const isControllableReason = (reason: string | null | undefined): boolean =>
  (reason ?? '').trim().toUpperCase().startsWith('C:');

/**
 * A controllable (C:) reason with no type is a hole in OTD — the failure is
 * counted as a Service Failure but can never count as late. Every edit surface
 * (Service Failures modal, Trouble Ticket modal incl. its Change All fan-out,
 * TTs & Service Failures board) refuses to save that combination, and the
 * backend refuses it on every reschedule write. The type follows the same
 * permission as the reason — whoever may set the reason sets the type with it;
 * there is no separate grant. Uncontrollable reasons may stay untyped.
 */
export const serviceFailureTypeMissing = (
  reason: string | null | undefined,
  type: string | null | undefined,
): boolean => isControllableReason(reason) && !(type ?? '').trim();

export const SERVICE_FAILURE_TYPE_REQUIRED_MESSAGE =
  'Service Failure Type is required for a controllable (C:) reason.';
